import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';

import { formatErrorMessages, removeUnderscores } from '../../../utils/globalHelpers';
import makeSelectMain, { getIsSso } from '../../Main/selectors';
import { getDesktopProfileProps, getMobileProfileProps } from './helpers';
import { initialState } from './reducer';

/* eslint-disable object-curly-newline */
import {
  selectRequestStatus as selectCommunicationsRequestStatus,
} from '../Communications/selectors';

/**
 * Direct selector to the Profile state domain
 */
const selectProfileDomain = (state) => state.profile || initialState;

/**
 * Direct selector to the intl object
 */
const selectIntl = (_, props) => props.intl;

/**
  * Other specific selectors
  */

const selectBannerError = () => createSelector(
  makeSelectProfile('errors'),
  ({ bannerError }) => bannerError,
);

const selectCurrentSetting = (prop) => createSelector(
  makeSelectProfile('currentSetting'),
  (currentSettingState) => currentSettingState[prop],
);

const selectDesktopProfileProps = () => createSelector(
  selectProfileProps(),
  makeSelectMain('isSslAuth'),
  getIsSso(),
  selectIntl,
  (
    profileProps,
    isSslAuth,
    isSso,
    { formatMessage },
  ) => getDesktopProfileProps(
    profileProps,
    isSslAuth,
    isSso,
    formatMessage,
  ),
);

const selectForm = (prop) => createSelector(
  makeSelectProfile('forms'),
  (forms) => forms[prop],
);

const selectFormConfirmation = (prop) => createSelector(
  makeSelectProfile('formConfirmations'),
  (formConfirmationsState) => formConfirmationsState[prop],
);

const selectFormErrors = (prop) => createSelector(
  makeSelectProfile('formErrors'),
  (formErrors) => formErrors[prop],
);

const selectIsSubmitDisabled = (prop) => createSelector(
  makeSelectProfile('forms'),
  makeSelectProfile('formErrors'),
  (forms, formErrors) => {
    const formToUse = forms[prop];
    const errorFormToUse = formErrors[prop];
    const isFormComplete = Object.keys(formToUse).every((field) => formToUse[field] !== '');
    const hasFormErrors = Object.keys(errorFormToUse).some((field) => errorFormToUse[field] !== '');
    if (isFormComplete) {
      return hasFormErrors;
    }
    return !isFormComplete;
  },
);

const selectMobileProfileProps = () => createSelector(
  selectProfileProps(),
  makeSelectMain('isSslAuth'),
  getIsSso(),
  selectIntl,
  (
    profileProps,
    isSslAuth,
    isSso,
    { formatMessage },
  ) => getMobileProfileProps(
    profileProps,
    isSslAuth,
    isSso,
    formatMessage,
  ),
);

const selectProfileScreenReaderNotifications = () => createSelector(
  makeSelectProfile('errors'),
  makeSelectProfile('formErrors'),
  (
    {
      bannerError,
      serviceErrors: {
        fetchLegalNameMilitaryStatus,
        fetchMobileNumberStatus,
        fetchProfile,
      },
    },
    { email, mobileNumber, password, uploadDocument, verifyMobileNumber },
  ) => {
    const nonFormErrors = [
      bannerError,
      fetchLegalNameMilitaryStatus,
      fetchMobileNumberStatus,
      fetchProfile,
    ];
    const notifications = nonFormErrors.filter((error) => !!error);
    return notifications.concat(formatErrorMessages({
      ...email,
      ...mobileNumber,
      ...password,
      ...uploadDocument,
      ...verifyMobileNumber,
    }));
  },
);

const selectProfileProps = () => createSelector(
  makeSelectProfile('currentSetting'),
  makeSelectProfile('legalName'),
  makeSelectProfile('militaryServiceStatus'),
  selectRequestStatus('fetchLegalNameMilitaryStatus'),
  selectRequestStatus('fetchProfile'),
  (
    { email, mobileNumber, username },
    legalName,
    militaryServiceStatus,
    fetchLegalNameMilitaryStatusRequestStatus,
    fetchProfileRequestStatus,
  ) => ({
    email: email || '',
    isNameMilitaryLoading: ['idle', 'loading'].includes(fetchLegalNameMilitaryStatusRequestStatus),
    isProfileLoading: ['idle', 'loading'].includes(fetchProfileRequestStatus),
    legalName,
    militaryServiceStatus:
      militaryServiceStatus !== 'No_Value_Assigned' ? removeUnderscores(militaryServiceStatus) : '',
    mobileNumber: mobileNumber || '',
    username: username || '',
  }),
);

const selectProfileSettings = (prop) => createSelector(
  makeSelectProfile('currentSetting'),
  (currentSetting) => {
    if (!isEmpty(currentSetting)) {
      return currentSetting[prop] || '';
    }
    return '';
  },
);

const selectRequestStatus = (prop) => createSelector(
  makeSelectProfile('requestStatus'),
  (requestStatusState) => requestStatusState[prop],
);

const selectServiceError = (prop) => createSelector(
  makeSelectProfile('errors'),
  ({ serviceErrors }) => serviceErrors[prop],
);

const selectSettingsHomeFetchError = () => createSelector(
  selectCommunicationsRequestStatus('fetchPaperlessSettingAll'),
  selectRequestStatus('fetchLegalNameMilitaryStatus'),
  selectRequestStatus('fetchMobileNumberStatus'),
  selectRequestStatus('fetchProfile'),
  (
    fetchPaperlessSettingAllRequestStatus,
    fetchLegalNameMilitaryStatusRequestStatus,
    fetchMobileNumberStatusRequestStatus,
    fetchProfileRequestStatus,
  ) => [
    fetchPaperlessSettingAllRequestStatus,
    fetchLegalNameMilitaryStatusRequestStatus,
    fetchMobileNumberStatusRequestStatus,
    fetchProfileRequestStatus,
  ].includes('failed'),
);

const selectDocumentRequestBody = (documentCategory) => createSelector(
  selectForm('uploadDocument'),
  ({ fileInput }) => {
    const { fileData, fileName, fileSize } = fileInput;
    return {
      document: fileData,
      documentCategory,
      documentName: fileName,
      documentSize: fileSize,
    };
  },
);

/**
 * Default selector used by Profile
 */
const makeSelectProfile = (prop) => createSelector(
  selectProfileDomain,
  (state) => state[prop],
);

export default makeSelectProfile;
export {
  selectBannerError,
  selectCurrentSetting,
  selectDesktopProfileProps,
  selectDocumentRequestBody,
  selectForm,
  selectFormConfirmation,
  selectProfileScreenReaderNotifications,
  selectFormErrors,
  selectIsSubmitDisabled,
  selectMobileProfileProps,
  selectProfileProps,
  selectProfileSettings,
  selectRequestStatus,
  selectServiceError,
  selectSettingsHomeFetchError,
};
